import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  directoryQueryIsActive,
  directoryQueryToSearchParams,
  searchParamsToDirectoryQuery,
  toggleSelected,
  type DirectoryQuery,
} from '../../lib/directory-query';

export function useDirectoryQuery<K extends string>(
  chipKeys: readonly K[],
  defaultOrderBy: string | null,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(
    () => searchParamsToDirectoryQuery(searchParams, chipKeys, defaultOrderBy),
    [searchParams, chipKeys, defaultOrderBy],
  );

  const apply = useCallback(
    (patch: Partial<Pick<DirectoryQuery<K>, 'query' | 'orderBy'>> & { selected?: Partial<Record<K, string[]>> }) => {
      const next: DirectoryQuery<K> = {
        query: patch.query !== undefined ? patch.query : state.query,
        orderBy: patch.orderBy !== undefined ? patch.orderBy : state.orderBy,
        selected: { ...state.selected, ...patch.selected },
      };
      setSearchParams(directoryQueryToSearchParams(searchParams, next, chipKeys, defaultOrderBy), {
        replace: true,
      });
    },
    [chipKeys, defaultOrderBy, searchParams, setSearchParams, state],
  );

  const toggleChip = useCallback(
    (key: K, value: string) => {
      apply({ selected: { [key]: toggleSelected(state.selected[key] ?? [], value) } as Partial<Record<K, string[]>> });
    },
    [apply, state.selected],
  );

  const clear = useCallback(() => {
    const empty = {} as Record<K, string[]>;
    for (const key of chipKeys) empty[key] = [];
    setSearchParams(
      directoryQueryToSearchParams(
        searchParams,
        { query: null, orderBy: defaultOrderBy, selected: empty },
        chipKeys,
        defaultOrderBy,
      ),
      { replace: true },
    );
  }, [chipKeys, defaultOrderBy, searchParams, setSearchParams]);

  return {
    query: state.query,
    orderBy: state.orderBy,
    selected: state.selected,
    apply,
    toggleChip,
    clear,
    isActive: directoryQueryIsActive(state, chipKeys, defaultOrderBy),
  };
}
